import { all, put, takeLatest, call, select } from 'redux-saga/effects'
import { push } from 'connected-react-router'
import exportFromJSON from 'export-from-json'

import * as actionTypes from './action-types'
import * as actionTypesError from '../errors/action-types'

import { getCPV, getSearchConfig } from '../config/selectors'

import { getSearches } from './selectors'
import {
  fetchSearches,
  removeSearches,
  fetchSearchDetails,
  fetchVendors,
  saveNewSearch,
  editSearch,
  addVendor,
} from './api'
import {
  getSearchesWithCPVInfo,
  formatSearchOptions,
  makeDataToExport,
  processServerError,
} from './business'

import arrayToObjectByKeyObject from 'utils/arrayToObjectByKeyObject'
import { showSnackbar } from '../snackbar/actions'

function* fetchSearchesSaga() {
  const CPV = yield select(getCPV)
  if (!CPV) return
  try {
    const searches = yield call(fetchSearches)
    const currentSearches = searches._embedded ? searches._embedded.searchs : []
    const searchesWithCPVInfo = yield call(
      getSearchesWithCPVInfo,
      currentSearches,
      CPV,
    )
    yield put({ type: actionTypes.SEARCHES_SUCCESS })
    yield put({ type: actionTypes.SET_SEARCHES, payload: searchesWithCPVInfo })
  } catch (e) {
    yield put({ type: actionTypes.SEARCHES_ERROR })
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* fakeRemoveSaga({ payload: { ids } }) {
  const searches = yield select(getSearches)
  const filteredSearches = searches.filter(search => !ids.includes(search.id))
  yield put({ type: actionTypes.SET_SEARCHES, payload: filteredSearches })
}

function* removeSaga({ payload: { ids } }) {
  try {
    yield call(removeSearches, ids)
    yield put({ type: actionTypes.REMOVE_SUCCESS })
    yield put({ type: actionTypes.FETCH_SEARCHES })
  } catch (e) {
    yield put({ type: actionTypes.REMOVE_ERROR })
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* fetchSearchDetailsSaga({
  payload: { id, page = 1, keyword = '', filters, data },
}) {
  try {
    const { _links, _embedded, ...filtersInfo } = yield call(
      id ? fetchSearchDetails : fetchVendors,
      {
        id,
        page,
        keyword,
        filters,
        data,
      },
    )

    const currentVendors = _embedded ? _embedded.vendors : []
    const results = yield call(arrayToObjectByKeyObject, currentVendors || [])
    yield put({ type: actionTypes.SEARCH_DETAILS_SUCCESS })
    yield put({
      type: actionTypes.SET_SEARCH_DETAILS_FILTERS,
      payload: filtersInfo,
    })
    yield put({
      type: actionTypes.SET_SEARCH_DETAILS_RESULTS,
      payload: results,
    })
  } catch (e) {
    const errors = yield call([e, 'json'])
    const proccesedErrors = processServerError(e.status, errors)
    if (proccesedErrors) {
      yield put({
        type: actionTypes.SEARCH_DETAILS_ERROR,
        payload: proccesedErrors,
      })
    } else {
      yield put({ type: actionTypesError.ERROR, payload: { error: e } })
    }
  }
}

function* getSearchOptionsSaga({
  payload: { id, name, keyword, filters, data },
}) {
  try {
    const response = yield call(id ? fetchSearchDetails : fetchVendors, {
      id,
      keyword,
      limit: 50,
      filters,
      data,
    })
    const vendors = formatSearchOptions(
      (response._embedded || {}).vendors,
      name,
    )

    yield put({ type: actionTypes.SET_SEARCH_OPTIONS, payload: vendors })
  } catch (e) {
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* saveSearchSaga({ payload: { id, data, name } }) {
  try {
    const response = yield call(id ? editSearch : saveNewSearch, {
      id,
      data,
      name,
    })

    yield put(showSnackbar({ message: 'searches:save-confirm' }))

    if (id) {
      yield put({ type: actionTypes.FETCH_SEARCH_DETAILS, payload: { id } })
    } else {
      const { id } = response
      yield put(push(`/app/searches/${id}`))
    }
  } catch (e) {
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* setIsVendorInMyNetworkSaga({ payload: { id, isInMyNetwork } }) {
  yield put({
    type: actionTypes.MERGE_SEARCH_DETAILS_RESULTS,
    payload: { id, data: { is_in_my_network: isInMyNetwork } },
  })
}

function* addVendorSaga({
  payload: { identification_code, mustShowSnackbar = false },
}) {
  try {
    yield call(addVendor, identification_code)
    yield put({ type: actionTypes.ADD_VENDOR_SUCCESS })
    if (mustShowSnackbar) {
      yield put(showSnackbar({ message: 'vendors:invitation-send' }))
    }
    yield put(push('/app/vendors/invitations'))
  } catch (e) {
    yield put({ type: actionTypes.ADD_VENDOR_ERROR })
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

function* exportSaga({
  payload: { id, keyword = '', filters, data, translator },
}) {
  try {
    const response = yield call(fetchVendors, {
      keyword,
      limit: 0,
      filters,
      data,
    })
    const config = yield select(getSearchConfig)
    const currentVendors = response._embedded ? response._embedded.vendors : []
    const dataToExport = yield call(
      makeDataToExport,
      currentVendors,
      config,
      translator,
    )

    const fileName = translator('csv:file-name')
    const exportType = 'xls'

    exportFromJSON({ data: dataToExport, fileName, exportType })

    yield put(showSnackbar({ message: 'search:downloaded' }))
  } catch (e) {
    yield put({ type: actionTypes.EXPORT_ERROR })
    yield put({ type: actionTypesError.ERROR, payload: { error: e } })
  }
}

export default function* rootSaga() {
  yield all([
    takeLatest(actionTypes.FETCH_SEARCHES, fetchSearchesSaga),
    takeLatest(actionTypes.FAKE_REMOVE, fakeRemoveSaga),
    takeLatest(actionTypes.REMOVE, removeSaga),
    takeLatest(actionTypes.FETCH_SEARCH_DETAILS, fetchSearchDetailsSaga),
    takeLatest(actionTypes.GET_SEARCH_OPTIONS, getSearchOptionsSaga),
    takeLatest(actionTypes.SAVE, saveSearchSaga),
    takeLatest(actionTypes.ADD_VENDOR, addVendorSaga),
    takeLatest(actionTypes.FAKE_ADD_VENDOR, setIsVendorInMyNetworkSaga),
    takeLatest(actionTypes.EXPORT, exportSaga),
  ])
}
